import {
  TransactionStatus,
  type ApiProblemDetails,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useTransactionInitiateMutation } from "Api/Mutations/Transactions/useTransactionInitiateMutation";
import { useAmount } from "Hooks/useAmount";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  PaymentDrawerForm,
  type PaymentSubmitData,
} from "Components/Chat/PaymentDrawerForm";
import { buildPaymentMessageText } from "Components/Chat/chatAttachmentTypes";
import type { Channel } from "stream-chat";

const RESOURCES = Resources.Chat.PaymentRequest.Drawer;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channel: Channel;
};

export const CreatePaymentRequestDrawer = ({
  isOpen,
  onClose,
  channel,
}: Props) => {
  const { t } = useResource();
  const toAmountWithCurrency = useAmount();
  // Chat gates on IdentityUser + verified phone, NOT on region — and useAmount throws for a
  // region-less session. Surface an error instead of crashing the drawer.
  const isRegionMissing = !useLoggedInUser()?.region;

  const {
    mutateAsync: initiateTransaction,
    isPending,
    error,
  } = useTransactionInitiateMutation();

  const handleSubmit = async ({ amount, bankAccountID }: PaymentSubmitData) => {
    if (isRegionMissing) {
      return;
    }
    const { transaction } = await initiateTransaction({
      amount: toAmountWithCurrency(amount),
      receiverBankAccountID: bankAccountID,
    });
    await channel.sendMessage({
      text: buildPaymentMessageText(t, {
        type: "payment_request",
        amount: transaction.amount.value,
        currencyCode: transaction.amount.currencyCode,
      }),
      attachments: [
        {
          type: "payment_request",
          amount: transaction.amount.value,
          currencyCode: transaction.amount.currencyCode,
          transactionPublicID: transaction.publicID,
          transactionStatus: TransactionStatus.Created,
        },
      ],
    });
    onClose();
  };

  return (
    <PaymentDrawerForm
      isOpen={isOpen}
      onClose={onClose}
      title={t(RESOURCES.Title)}
      bankAccountLabel={t(RESOURCES.BankAccount)}
      amountLabel={t(RESOURCES.Amount)}
      noteLabel={t(RESOURCES.Note)}
      notePlaceholder={t(RESOURCES.NotePlaceholder)}
      submitLabel={t(RESOURCES.Submit)}
      isPending={isPending}
      error={error || isRegionMissing}
      errorContent={
        (error as { data?: ApiProblemDetails } | null)?.data?.errors
          ?.currencyCode
          ? t(RESOURCES.InvalidCurrency)
          : t(RESOURCES.Error)
      }
      onSubmit={handleSubmit}
    />
  );
};
